// Advanced Mode screen text (called "Live Interview" until 2026-10-31; "live" in code) (LIVE_INTERVIEW_PLAN.md §8, 2026-10-30), in the account's
// interface language. The interview itself (questions, answers) is always English.

export const STRINGS = {
  en: {
    eyebrow: "Advanced Mode",
    startTitle: "Ready for your Advanced Mode interview?",
    startBody: [
      "This works like a real interview: you'll hear each question, with no text on screen.",
      "After each question you have 30 seconds to start answering (20 for a follow-up). Once you start speaking, take as long as you need.",
      "When you stop speaking for a few seconds, the interviewer moves on. You can also press “I’m finished”.",
      "To hear a question again, just ask: “Could you repeat the question?”",
    ],
    startButton: "Start the interview",
    micNeeded: "Allow microphone access when your browser asks.",
    questionOf: (n, total) => `Question ${n} of ${total}`,
    followUp: "Follow-up question",
    closing: "Before we finish",
    speaking: "The interviewer is speaking…",
    startIn: (s) => `Start answering in ${s} s`,
    listening: "Listening…",
    stillListening: "Still listening… press “I’m finished” when you’re done.",
    sending: "One moment…",
    finished: "I’m finished",
    completing: "Preparing your report…",
    micFailTitle: "Advanced Mode can’t use your microphone",
    micFailBody: "Your browser blocked the microphone or can’t record here. You can continue this interview in Guided Mode, where you press Record and Done for each answer.",
    micFailGuided: "Continue in Guided Mode",
    micFailRetry: "Try again",
    sendFail: "We couldn’t send your answer. Check your connection; trying again…",
    reportFail: "Could not build the report yet.",
  },
  "pt-BR": {
    eyebrow: "Modo Avançado",
    startTitle: "Pronto para a sua entrevista no Modo Avançado?",
    startBody: [
      "Funciona como uma entrevista real: você ouvirá cada pergunta, sem texto na tela.",
      "Depois de cada pergunta, você tem 30 segundos para começar a responder (20 para uma pergunta de acompanhamento). Quando começar a falar, use o tempo que precisar.",
      "Quando você parar de falar por alguns segundos, o entrevistador continua. Você também pode pressionar “Terminei”.",
      "Para ouvir uma pergunta de novo, é só pedir: “Could you repeat the question?”",
    ],
    startButton: "Começar a entrevista",
    micNeeded: "Permita o acesso ao microfone quando o navegador pedir.",
    questionOf: (n, total) => `Pergunta ${n} de ${total}`,
    followUp: "Pergunta de acompanhamento",
    closing: "Antes de terminar",
    speaking: "O entrevistador está falando…",
    startIn: (s) => `Comece a responder em ${s} s`,
    listening: "Ouvindo…",
    stillListening: "Ainda ouvindo… pressione “Terminei” quando acabar.",
    sending: "Um momento…",
    finished: "Terminei",
    completing: "Preparando o seu relatório…",
    micFailTitle: "O Modo Avançado não consegue usar o seu microfone",
    micFailBody: "O navegador bloqueou o microfone ou não consegue gravar aqui. Você pode continuar esta entrevista no Modo Guiado, em que você pressiona Gravar e Concluir em cada resposta.",
    micFailGuided: "Continuar no Modo Guiado",
    micFailRetry: "Tentar novamente",
    sendFail: "Não conseguimos enviar a sua resposta. Verifique a conexão; tentando novamente…",
    reportFail: "Não foi possível gerar o relatório ainda.",
  },
  "es-419": {
    eyebrow: "Modo Avanzado",
    startTitle: "¿Listo para su entrevista en Modo Avanzado?",
    startBody: [
      "Funciona como una entrevista real: escuchará cada pregunta, sin texto en la pantalla.",
      "Después de cada pregunta tiene 30 segundos para empezar a responder (20 para una pregunta de seguimiento). Cuando empiece a hablar, tómese el tiempo que necesite.",
      "Cuando deje de hablar por unos segundos, el entrevistador continúa. También puede presionar “Terminé”.",
      "Para escuchar una pregunta otra vez, solo pídalo: “Could you repeat the question?”",
    ],
    startButton: "Comenzar la entrevista",
    micNeeded: "Permita el acceso al micrófono cuando el navegador lo pida.",
    questionOf: (n, total) => `Pregunta ${n} de ${total}`,
    followUp: "Pregunta de seguimiento",
    closing: "Antes de terminar",
    speaking: "El entrevistador está hablando…",
    startIn: (s) => `Empiece a responder en ${s} s`,
    listening: "Escuchando…",
    stillListening: "Sigo escuchando… presione “Terminé” cuando acabe.",
    sending: "Un momento…",
    finished: "Terminé",
    completing: "Preparando su informe…",
    micFailTitle: "El Modo Avanzado no puede usar su micrófono",
    micFailBody: "El navegador bloqueó el micrófono o no puede grabar aquí. Puede continuar esta entrevista en el Modo Guiado, donde presiona Grabar y Listo en cada respuesta.",
    micFailGuided: "Continuar en Modo Guiado",
    micFailRetry: "Intentar de nuevo",
    sendFail: "No pudimos enviar su respuesta. Revise su conexión; intentando de nuevo…",
    reportFail: "Todavía no se pudo generar el informe.",
  },
  "fr-CA": {
    eyebrow: "Mode avancé",
    startTitle: "Prêt pour votre entrevue en mode avancé?",
    startBody: [
      "Cela fonctionne comme une vraie entrevue : vous entendrez chaque question, sans texte à l’écran.",
      "Après chaque question, vous avez 30 secondes pour commencer à répondre (20 pour une question de suivi). Une fois que vous parlez, prenez le temps qu’il vous faut.",
      "Quand vous arrêtez de parler quelques secondes, l’intervieweur continue. Vous pouvez aussi appuyer sur « J’ai terminé ».",
      "Pour réentendre une question, demandez simplement : “Could you repeat the question?”",
    ],
    startButton: "Commencer l’entrevue",
    micNeeded: "Autorisez l’accès au micro lorsque votre navigateur le demande.",
    questionOf: (n, total) => `Question ${n} de ${total}`,
    followUp: "Question de suivi",
    closing: "Avant de terminer",
    speaking: "L’intervieweur parle…",
    startIn: (s) => `Commencez à répondre dans ${s} s`,
    listening: "À l’écoute…",
    stillListening: "Toujours à l’écoute… appuyez sur « J’ai terminé » quand vous avez fini.",
    sending: "Un instant…",
    finished: "J’ai terminé",
    completing: "Préparation de votre rapport…",
    micFailTitle: "Le mode avancé ne peut pas utiliser votre micro",
    micFailBody: "Votre navigateur a bloqué le micro ou ne peut pas enregistrer ici. Vous pouvez poursuivre cette entrevue en mode guidé, où vous appuyez sur Enregistrer et Terminé pour chaque réponse.",
    micFailGuided: "Continuer en mode guidé",
    micFailRetry: "Réessayer",
    sendFail: "Nous n’avons pas pu envoyer votre réponse. Vérifiez votre connexion; nouvel essai…",
    reportFail: "Le rapport n’est pas encore prêt.",
  },
};

export function liveStrings(lang) {
  return STRINGS[lang] || STRINGS.en;
}

export const LIVE_LANGS = Object.keys(STRINGS);
